import { useMemo } from 'react';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import SubscriptionModal from 'src/components/VividiOnly/Subscription';
import { useHasInventoryItem } from 'src/hooks/useInventoryItem';
import { userInventoryAtom } from 'src/stores/auth/atoms';
import { getRequiredGemFromMatchFilterAtom, matchFiltersAtom, matchFilterSelectionsAtom } from 'src/stores/match/atoms';
import { showModalAtom } from 'src/stores/modal/atoms';
import { MatchFilterOption } from 'src/types/Match';
import { ModalType } from 'src/types/Modal';
export const FREE_GENDER_FILTER_OPTIONS = ['ALL', 'BOTH'];
const genderFilterSelector = ({
  filterId
}: {
  filterId: string;
}) => filterId === 'GENDER_CHOICE';
const useGenderPreference = () => {
  const hasSubscription = useHasInventoryItem('VIVIDI_UNLIMITED');
  const inventoryItems = useAtomValue(userInventoryAtom);
  const matchFilters = useAtomValue(matchFiltersAtom);
  const [matchFilterSelections, setMatchFilterSelections] = useAtom(matchFilterSelectionsAtom);
  const getRequiredGemFromMatchFilter = useSetAtom(getRequiredGemFromMatchFilterAtom);
  const genderMatchFilter = matchFilters?.find(genderFilterSelector);
  const genderMatchFilterSelection = matchFilterSelections.find(genderFilterSelector);
  const genderMatchFilterOption = genderMatchFilter?.filterOptions.find(({
    key
  }) => key === genderMatchFilterSelection?.optionKey);
  const showModal = useSetAtom(showModalAtom);
  const genderFilterItemQuantity = useMemo(() => {
    if (!genderMatchFilter) return 0;
    return inventoryItems.reduce((sum, {
      categoryId,
      quantity
    }) => {
      if (categoryId !== genderMatchFilter.consumableCategoryId) {
        return sum;
      }
      return sum + (quantity ?? 0);
    }, 0);
  }, [inventoryItems, genderMatchFilter]);
  const getRequiredGemsForGenderOption = (genderFilterOption: MatchFilterOption) => {
    return getRequiredGemFromMatchFilter([{
      filterId: 'GENDER_CHOICE',
      optionKey: genderFilterOption.key,
      optionGemCost: genderFilterOption.gemCost
    }]);
  };
  const tryApplyGenderFilterOption = ({
    genderFilterOption
  }: {
    genderFilterOption: MatchFilterOption;
  }) => {
    const nextMatchFilterSelections = matchFilterSelections.map(matchFilterSelection => {
      if (matchFilterSelection.filterId !== 'GENDER_CHOICE') {
        return matchFilterSelection;
      }
      return {
        ...matchFilterSelection,
        optionKey: genderFilterOption.key,
        optionGemCost: genderFilterOption.gemCost
      };
    });
    const isValid = FREE_GENDER_FILTER_OPTIONS.includes(genderFilterOption.key) || hasSubscription;
    if (isValid) {
      setMatchFilterSelections(nextMatchFilterSelections);
    } else {
      showModal({
        key: ModalType.SUBSCRIPTION_POPUP,
        component: () => <SubscriptionModal referrer='genderfilter' />
      });
    }
  };
  return {
    genderMatchFilter,
    genderMatchFilterSelection,
    genderMatchFilterOption,
    getRequiredGemsForGenderOption,
    tryApplyGenderFilterOption,
    genderFilterItemQuantity
  };
};
export default useGenderPreference;